import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { getArtworkImageSrc } from '../utils/artworkImage';

const CartContext = createContext();
const GUEST_CART_KEY = 'cart_guest';

export function CartProvider({ children }) {
  const { user } = useAuthContext();
  const userId = user?._id || user?.id;

  const [cartItems, setCartItems] = useState([]);
  const [loadedUserId, setLoadedUserId] = useState(null);

  useEffect(() => {
    if (userId) {
      const savedCart = localStorage.getItem(`cart_${userId}`);
      const guestCart = localStorage.getItem(GUEST_CART_KEY);
      let nextCart = [];

      try {
        nextCart = savedCart ? JSON.parse(savedCart) : [];
      } catch {
        localStorage.removeItem(`cart_${userId}`);
      }

      if (guestCart) {
        try {
          const parsedGuestCart = JSON.parse(guestCart);
          const merged = [...nextCart];
          parsedGuestCart.forEach((item) => {
            if (!merged.some((existing) => existing._id === item._id)) {
              merged.push(item);
            }
          });
          nextCart = merged;
          localStorage.removeItem(GUEST_CART_KEY);
        } catch {
          localStorage.removeItem(GUEST_CART_KEY);
        }
      }

      setCartItems(nextCart);
      setLoadedUserId(userId);
      return;
    }

    const guestCart = localStorage.getItem(GUEST_CART_KEY);
    try {
      setCartItems(guestCart ? JSON.parse(guestCart) : []);
    } catch {
      localStorage.removeItem(GUEST_CART_KEY);
      setCartItems([]);
    }
    setLoadedUserId(null);
  }, [userId]);

  useEffect(() => {
    if (loadedUserId === null) {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
      return;
    }
    if (userId && loadedUserId === userId) {
      localStorage.setItem(`cart_${userId}`, JSON.stringify(cartItems));
    }
  }, [cartItems, userId, loadedUserId]);

  const addToCart = (artwork) => {
    setCartItems((prev) => {
      if (prev.find((item) => item._id === artwork._id)) return prev;
      return [
        ...prev,
        {
          ...artwork,
          imageUrl: getArtworkImageSrc(artwork),
          quantity: 1,
        },
      ];
    });
  };

  const removeFromCart = (artworkId) => {
    setCartItems((prev) => prev.filter((item) => item._id !== artworkId));
  };

  const clearCart = useCallback(() => {
    setCartItems([]);
    if (userId) {
      localStorage.removeItem(`cart_${userId}`);
    } else {
      localStorage.removeItem(GUEST_CART_KEY);
    }
  }, [userId]);

  const getCartTotal = () =>
    cartItems.reduce(
      (total, item) =>
        total +
        parseFloat(String(item.price ?? 0).replace(/[^0-9.]/g, '') || 0) *
          (item.quantity || 1),
      0
    );

  const isInCart = (artworkId) => cartItems.some((item) => item._id === artworkId);

  return (
    <CartContext.Provider
      value={{ cartItems, addToCart, removeFromCart, clearCart, getCartTotal, isInCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
